import { createAction, props } from '@ngrx/store';

import type { ConflictComponent, ConflictDetails, Vehicle } from 'src/app/api/owcc/models';
import type { FeasibilityResultAlternative } from 'src/app/shared/feasibility-conflict-modal/feasibility-result-alternative.interface';
import type { FeasibilityResult } from 'src/app/shared/feasibility-conflict-modal/feasibility-result.interface';
import type { CategoryFixationLinkData } from 'src/app/utils/models/category-fixation-link.interface';
import type { EnrichedComponentCategory } from 'src/app/utils/models/enriched-component-category.type';
import type { ExtendedVehicleComponentInfoCollection } from 'src/app/utils/models/extended-vehicle-component-info-collection.interface';
import type { VehicleComponentCollection } from 'src/app/utils/models/vehicle-component-collection.interface';

import type { ConfigurationSelectableChange } from './utils/configuration-selectable-change.interface';

export enum ConfigurationActionType {
  StartInitialize = '[Configuration] START Initialize',
  FinishInitialize = '[Configuration] FINISH Initialize',
  AcceptChanges = '[Configuration] ACCEPT Changes to Configuration',
  StartLoadVehicle = '[Configuration] START Load Vehicle',
  FinishLoadVehicle = '[Configuration] FINISH Load Vehicle',
  StartLoadComponentInfo = '[Configuration] START Load ComponentInfo',
  FinishLoadComponentInfo = '[Configuration] FINISH Load ComponentInfo',
  StartUpdateVehicleContext = '[Configuration] START Update Vehicle Context',
  FinishUpdateVehicleContext = '[Configuration] FINISH Update Vehicle Context',
  StartUpdateLocationState = '[Configuration] START Update Location State',
  FinishUpdateLocationState = '[Configuration] FINISH Update Location State',
  StartLoadSelectables = '[Configuration] START Load Selectables',
  FinishLoadSelectables = '[Configuration] FINISH Load Selectables',
  StartEnqueueSelectableChange = '[Configuration] START Enqueue Selectable Change',
  FinishEnqueueSelectableChange = '[Configuration] FINISH Enqueue Selectable Change',
  StartRequestSelectableChangeFromQueue = '[Configuration] START Request Selectable Change from Queue',
  FinishRequestSelectableChangeFromQueue = '[Configuration] FINISH Request Selectable Change from Queue',
  StartProcessSelectableChange = '[Configuration] START Process Selectable Change',
  FinishProcessSelectableChange = '[Configuration] FINISH Process Selectable Change',
  StartLoadFeasibilityResult = '[Configuration] START Load Feasibility Result',
  FinishLoadFeasibilityResult = '[Configuration] FINISH Load Feasibility Result',
  StartProcessFeasibilityResult = '[Configuration] START Process Feasibility Result',
  FinishProcessFeasibilityResult = '[Configuration] FINISH Process Feasibility Result',
  StartUpdateFeasibleVehicle = '[Configuration] START Update Feasible Vehicle',
  FinishUpdateFeasibleVehicle = '[Configuration] FINISH Update Feasible Vehicle',
  StartResolveFeasibilityConflict = '[Configuration] START Resolve Feasibility Conflict',
  FinishResolveFeasibilityConflict = '[Configuration] FINISH Resolve Feasibility Conflict',
  StartProcessFeasibilityConflictResult = '[Configuration] START Process Feasibility Conflict Result',
  FinishProcessFeasibilityConflictResult = '[Configuration] FINISH Process Feasibility Conflict Result',
  StartUpdateVehicle = '[Configuration] START Update Vehicle',
  FinishUpdateVehicle = '[Configuration] FINISH Update Vehicle',
  OnlineCodeSectionInViewport = '[Configuration] Onlinecode Section Entered Viewport',
  UpdateSelectedCategories = '[Configuration] UPDATE Selected Categories',
  UpdateIsFilteredByCategory = '[Configuration] UPDATE is Filtered by Category',
  UpdateIsSimilarStockCarsAvailable = '[Configuration] UPDATE is similar stock cars available',
  AbortFeasibilityConflict = '[Configuration] ABORT Feasibility Conflict',
  ResetVehicle = '[Configuration] RESET Vehicle',
  UpdateIsInitialMotorization = '[Configuration] Update Is Initial Motorization'
}

export const startInitializeAction = createAction(ConfigurationActionType.StartInitialize);

export const finishInitializeAction = createAction(ConfigurationActionType.FinishInitialize);

/**
 * Action to be called when the user accepts unintended changes
 * Changes can be caused by:
 * - a conflict
 * - a motorization transfer
 * - entering with an OnlineCode or Baumuster
 */
export const acceptChangesAction = createAction(
  ConfigurationActionType.StartLoadVehicle,
  props<{
    vehicleId: string;
  }>()
);

export const startLoadVehicle = createAction(
  ConfigurationActionType.StartLoadVehicle,
  props<{
    vehicleId: string;
  }>()
);

export const finishLoadVehicleAction = createAction(
  ConfigurationActionType.FinishLoadVehicle,
  props<{
    vehicle: Vehicle;
  }>()
);

export const startLoadComponentInfoForCurrentVehicleAction = createAction(
  ConfigurationActionType.StartLoadComponentInfo,
  props<{
    modelId: string;
    vehicleId: string;
  }>()
);

export const finishLoadComponentInfoForCurrentVehicleAction = createAction(
  ConfigurationActionType.FinishLoadComponentInfo,
  props<{
    componentInfo: ExtendedVehicleComponentInfoCollection;
    vehicleId: string;
  }>()
);

export const startUpdateVehicleContextAction = createAction(
  ConfigurationActionType.StartUpdateVehicleContext,
  props<{
    vehicleBody: string;
    vehicleClass: string;
    vehicleId: string;
  }>()
);

export const finishUpdateVehicleContextAction = createAction(ConfigurationActionType.FinishUpdateVehicleContext);

export const startUpdateLocationStateAction = createAction(
  ConfigurationActionType.StartUpdateLocationState,
  props<{
    vehicleId: string;
  }>()
);

export const finishUpdateLocationStateAction = createAction(ConfigurationActionType.FinishUpdateLocationState);

export const startLoadSelectablesAction = createAction(
  ConfigurationActionType.StartLoadSelectables,
  props<{
    vehicleId: string;
  }>()
);

export const finishLoadSelectablesAction = createAction(
  ConfigurationActionType.FinishLoadSelectables,
  props<{
    componentCategories: EnrichedComponentCategory[];
    components: VehicleComponentCollection;
  }>()
);

export const startEnqueueSelectableChangeAction = createAction(
  ConfigurationActionType.StartEnqueueSelectableChange,
  props<{
    selectableChange: ConfigurationSelectableChange;
    source: string;
  }>()
);

export const finishEnqueueSelectableChangeAction = createAction(
  ConfigurationActionType.FinishEnqueueSelectableChange,
  props<{
    selectableChange: ConfigurationSelectableChange;
  }>()
);

export const startRequestSelectableChangeFromQueueAction = createAction(
  ConfigurationActionType.StartRequestSelectableChangeFromQueue
);

export const finishRequestSelectableChangeFromQueueAction = createAction(
  ConfigurationActionType.FinishRequestSelectableChangeFromQueue
);

export const startProcessSelectableChangeAction = createAction(
  ConfigurationActionType.StartProcessSelectableChange,
  props<{
    selectableChange: ConfigurationSelectableChange;
  }>()
);

export const finishProcessSelectableChangeAction = createAction(ConfigurationActionType.FinishProcessSelectableChange);

export const startLoadFeasibilityResultAction = createAction(
  ConfigurationActionType.StartLoadFeasibilityResult,
  props<{
    selectableChange: ConfigurationSelectableChange;
  }>()
);

export const finishLoadFeasibilityResultAction = createAction(
  ConfigurationActionType.FinishLoadFeasibilityResult,
  props<{
    feasibilityResult: FeasibilityResult;
  }>()
);

export const startProcessFeasibilityResultAction = createAction(
  ConfigurationActionType.StartProcessFeasibilityResult,
  props<{
    feasibilityResult: FeasibilityResult;
  }>()
);

export const finishProcessFeasibilityResultAction = createAction(
  ConfigurationActionType.FinishProcessFeasibilityResult
);

export const startUpdateFeasibleVehicleAction = createAction(
  ConfigurationActionType.StartUpdateFeasibleVehicle,
  props<{
    vehicle: Vehicle;
    conflictItem: ConflictComponent;
  }>()
);

export const abortFeasibilityConflictAction = createAction(
  ConfigurationActionType.AbortFeasibilityConflict,
  props<{
    feasibilityResult: FeasibilityResult;
  }>()
);

export const finishUpdateFeasibleVehicleAction = createAction(
  ConfigurationActionType.FinishUpdateFeasibleVehicle,
  props<{
    vehicleId?: string;
    vehicle: Vehicle;
  }>()
);

export const startResolveFeasibilityConflictAction = createAction(
  ConfigurationActionType.StartResolveFeasibilityConflict,
  props<{
    alternatives: FeasibilityResultAlternative[];
    conflictItem: ConflictComponent;
    vehicle: Vehicle;
    conflictDetails: ConflictDetails;
  }>()
);

export const finishResolveFeasibilityConflictAction = createAction(
  ConfigurationActionType.FinishResolveFeasibilityConflict,
  props<{
    vehicleId: string;
    feasibilityResult: FeasibilityResult;
  }>()
);

export const startProcessFeasibilityConflictResultAction = createAction(
  ConfigurationActionType.StartProcessFeasibilityConflictResult,
  props<{
    vehicleId: string;
    feasibilityResult: FeasibilityResult;
  }>()
);

export const finishProcessFeasibilityConflictResultAction = createAction(
  ConfigurationActionType.FinishProcessFeasibilityConflictResult
);

export const startUpdateVehicleAction = createAction(
  ConfigurationActionType.StartUpdateVehicle,
  props<{
    vehicle: Vehicle;
  }>()
);

export const finishUpdateVehicleAction = createAction(
  ConfigurationActionType.FinishUpdateVehicle,
  props<{
    vehicle: Vehicle;
  }>()
);

export const onlineCodeSectionInViewportAction = createAction(ConfigurationActionType.OnlineCodeSectionInViewport);

export const updateSelectedCategories = createAction(
  ConfigurationActionType.UpdateSelectedCategories,
  props<{
    selectedCategories: CategoryFixationLinkData[];
  }>()
);

export const updateIsFilteredByCategory = createAction(
  ConfigurationActionType.UpdateIsFilteredByCategory,
  props<{
    isFilteredByCategory: boolean;
  }>()
);

export const updateIsSimilarStockCarsAvailable = createAction(
  ConfigurationActionType.UpdateIsSimilarStockCarsAvailable,
  props<{
    isSimilarStockCarsAvailable: boolean;
  }>()
);

export const resetVehicleAction = createAction(ConfigurationActionType.ResetVehicle);

export const updateIsInitialMotorizationAction = createAction(
  ConfigurationActionType.UpdateIsInitialMotorization,
  props<{
    isInitialMotorization: boolean;
  }>()
);
